import type { Client, EventDispatcher } from '@optimizely/optimizely-sdk';
import type { OptimizelyDataFile } from './optimizely.types';
import {
    createInstance,
    setLogger as setOptimizelyLogLevel
} from '@optimizely/optimizely-sdk/dist/optimizely.lite.min.js';
import { getOptimizelyOverrides } from './optimizelyOverrides';
import {
    combineMaps,
    flattenMap,
    OptimizelyState,
    OptimizelyStateMap
} from './state';

type SimpleRequestObject = {
    cookies: Record<string, string | undefined>;
    headers: Record<string, string | string[] | undefined>;
    url?: string;
};

const VERCEL_EDGE_CLIENT_ENGINE = 'javascript-sdk/vercel-edge';

/* this file should only ever execute on the server */
if (undefined === typeof window && setOptimizelyLogLevel) {
    setOptimizelyLogLevel(null);
}

/* fetch is a global in middleware runtime (edge) but not SSR runtime (lambda) */
let fetch = globalThis.fetch;
if (!globalThis.fetch) {
    fetch = require('isomorphic-unfetch');
}

/* create a universal eventDispatcher using the now ponyfilled fetch */
export const eventDispatcher: EventDispatcher = {
    dispatchEvent: (eventObj, callback) => {
        if (eventObj.httpVerb !== 'POST') {
            return;
        }
        const dataString = JSON.stringify(eventObj.params);
        const req = fetch(eventObj.url, {
            method: 'POST',
            headers: {
                'content-type': 'application/json',
                'content-length': dataString.length.toString()
            },
            body: dataString
        }).then((response) => {
            if (response.ok) {
                callback({ statusCode: response.status });
            }
        });
        return req;
    }
};

export const createOptimizelyClient = async (
    edgeRuntime?: boolean
): Promise<Client> => {
    let datafile: OptimizelyDataFile;

    // use edge compatible fetch if used in edge runtime (middleware)
    if (edgeRuntime) {
        try {
            datafile = await fetch(
                `${process.env['NEXT_PUBLIC_OPTIMIZELY_URL']}/datafiles/${process.env['NEXT_PUBLIC_OPTIMIZELY_KEY']}.json`
            ).then((res) => res.ok && res.json());
        } catch (e) {
            datafile = { version: '4' };
        }
    } else {
        // eslint-disable-next-line @typescript-eslint/no-var-requires
        const { getDataFile } = require('./optimizelyDataFile');
        datafile = await getDataFile();
    }

    return createInstance({
        datafile,
        eventDispatcher,
        clientEngine: VERCEL_EDGE_CLIENT_ENGINE
    }) as Client;
};

/**
 * Returns relevant optimizely state for a given request and a promise for
 * event flushing that should be awaited according to request lifecycle.
 * Must be called on server, either in data-fetching methods, api or middleware.
 *
 * **userId must exist:** either provided at `req.cookies['ajs_anonymous_id']`
 * or generated and passed as param. If generating ensure persisted to session
 * with Set-Cookie header or writing to document.cookie
 */
export const getOptimizelyState = async (
    req: SimpleRequestObject,
    anonId?: string,
    edgeRuntime = false
): Promise<
    [optimizelyState: OptimizelyState, flushPromise: Promise<unknown>]
> => {
    const params = parseParams(req?.url ?? '');
    const ua = parseHeader(req?.headers?.['user-agent']);
    const cookies = req.cookies ?? {};
    const userId = anonId ?? cookies['ajs_anonymous_id'];

    const allOverridesMap: OptimizelyStateMap = getOptimizelyOverrides(
        req.cookies,
        params
    );

    if (!userId) {
        return [flattenMap(allOverridesMap), Promise.resolve()];
    }

    const attributes = ua ? { $opt_user_agent: ua } : undefined;

    const optimizelyClientInstance = await createOptimizelyClient(edgeRuntime);

    const { success } = await optimizelyClientInstance.onReady({
        timeout: 500
    });

    if (!success) {
        return [flattenMap(allOverridesMap), optimizelyClientInstance.close()];
    }

    const { experimentsMap, featuresMap } =
        optimizelyClientInstance.getOptimizelyConfig() ?? {};

    const scopedStateMap: OptimizelyStateMap = {
        experiments: {},
        features: {}
    };

    for (const key in featuresMap) {
        const enabled = optimizelyClientInstance.isFeatureEnabled(
            key,
            userId,
            attributes
        );
        if (enabled) scopedStateMap.features[key] = true;
    }

    for (const key in experimentsMap) {
        const variant = optimizelyClientInstance.getVariation(
            key,
            userId,
            attributes
        );
        if (variant !== null) scopedStateMap.experiments[key] = variant;
    }

    return [
        flattenMap(combineMaps(scopedStateMap, allOverridesMap)),
        optimizelyClientInstance.close()
    ];
};

/**
 * TODO: remove in favour of having the withSSROptimizely wrapper adding it to
 * context by default
 */
export const getOptimizelyFeatureFlagOnServer = async (
    req: SimpleRequestObject,
    featureKey: string,
    anonId?: string
) => {
    const [state, flushPromise] = await getOptimizelyState(req, anonId);
    await flushPromise;
    return state.includes(featureKey);
};

function parseParams(url: string): Record<string, string> {
    const params: Record<string, string> = {};
    try {
        const { searchParams } = new URL(url, 'https://www.zoopla.co.uk');
        for (const [key, value] of (
            searchParams as unknown as { entries(): Iterable<[string, string]> }
        ).entries()) {
            params[key] = value;
        }
        return params;
    } catch (e) {
        return params;
    }
}

function parseHeader(headerVal?: string | string[]) {
    return Array.isArray(headerVal) ? headerVal[0] : headerVal;
}
